/**
 * The decrypted fields-schema, read the same way by every signer's page:
 * demographic seeding, the signer's signature lines, and the date box paired
 * with each line. Lifted out of BridgeFieldsForm so the plan holder's journey
 * (PlanMemberJourney) reads the schema by the same rules instead of a copy.
 *
 * Pure apart from `loadFieldsSchema`, which fetches and decrypts.
 */
import { formRefusalOf, formRefusedError, openSecretBox } from "./bridgeClient";
import type { BBox, BridgeQuestion, PatientProfile } from "../types/shared";

// Schema entries arrive loosely typed (strings on v1, objects on v2) and the
// seeding writes `value` onto them in place, as BridgeFieldsForm always has.
type RawField = any;

export interface SignatureLine {
  name: string;
  page: number;
  bbox: BBox;
}

export interface ParsedFieldsSchema {
  fields: RawField[];
  schemaVersion: number;
  readerSubjects: string[];
  questions: BridgeQuestion[] | null;
  signatureField: SignatureLine | null;
}

export function parseFieldsSchema(parsed: any): ParsedFieldsSchema {
  const fields = Array.isArray(parsed?.fields) ? parsed.fields : [];
  const schemaVersion = Number(parsed?.schema_version) >= 2 ? 2 : 1;
  // WHICH SUBJECTS THE READER OF THIS LEG IS (backend
  // `questionnaire_builder.reader_subjects`). Absent on payloads sealed
  // before 2026-09-18 → both, which is the old behaviour exactly.
  let readerSubjects: string[] = ["patient", "plan_member"];
  if (Array.isArray(parsed?.reader_subjects) && parsed.reader_subjects.length) {
    readerSubjects = parsed.reader_subjects.map((x: any) => String(x));
  }
  const questions = Array.isArray(parsed?.questions) ? (parsed.questions as BridgeQuestion[]) : null;
  let signatureField: SignatureLine | null = null;
  const sf = parsed?.signature_field;
  if (sf && Array.isArray(sf.bbox) && sf.bbox.length >= 4 && Number.isFinite(Number(sf.page))) {
    signatureField = { name: sf.name, page: Number(sf.page), bbox: sf.bbox.map((v: any) => Number(v)) as BBox };
  }
  return { fields, schemaVersion, readerSubjects, questions, signatureField };
}

/** Fetch and decrypt this session's fields-schema. Null when the session has
 *  no key (nothing sealed to read). A fee refusal (409 `fee_required` /
 *  `fee_declined` / `request_closed`, ruling 2026-10-14) throws an error that
 *  carries the code (`formRefusalCodeOf`). */
export async function loadFieldsSchema(session: {
  sessionToken?: string;
  assignmentId?: string;
  symmetricKeyB64?: string;
}): Promise<ParsedFieldsSchema | null> {
  if (!session.symmetricKeyB64) return null;
  const resp = await fetch(
    `/api/bridge/assignments/${encodeURIComponent(session.assignmentId as string)}/fields-schema`,
    { method: "GET", headers: { "X-Patient-Token": session.sessionToken as string }, credentials: "omit" }
  );
  if (!resp.ok) {
    const message = `fields-schema fetch failed (${resp.status})`;
    const refusal = await formRefusalOf(resp);
    throw refusal ? formRefusedError(message, refusal) : new Error(message);
  }
  const body = await resp.json();
  if (!body.encrypted_b64) return parseFieldsSchema({});
  const plaintext = openSecretBox(body.encrypted_b64, session.symmetricKeyB64);
  return parseFieldsSchema(JSON.parse(new TextDecoder().decode(plaintext)));
}

/**
 * Seed confirmed demographics into the schema field values so the
 * questionnaire treats them as already answered — showing them on review
 * instead of re-asking name / DOB / address / etc. Mutates `schemaFields`.
 */
/** A phone box that names itself a WORK (or business) number — never seeded
 *  from the chart's one untyped phone (case imp_a2bb0615). Mirrors
 *  `is_work_phone_box` in routers/bridge.py. */
export const isWorkPhoneBox = (name: string): boolean =>
  /(^|_)(work|business)(_|$)/.test(String(name || "").toLowerCase());

/** A box about ANOTHER plan (coordination of benefits). Never seeded from the
 *  patient's own plan: the primary plan's sponsor or certificate in the other
 *  plan's box falsely claims dual coverage (principle 16). */
const isOtherPlanBox = (name: string, binding: string): boolean =>
  /(^|[._])other_/.test(binding) || /(^|_)other_(group_)?(plan|insur|coverage|benefit|carrier)/.test(name);

/**
 * The first page's answers about the patient's OWN plan that a carrier's form
 * asks again in its own words (imp_5559e114, Michael 2026-10-28: "don't want
 * people to be asked the same question twice"). Read by what the box IS — its
 * field-dictionary binding — and by its name only on links sealed before the
 * binding was carried:
 *   employer / plan sponsor  ← "Plan Sponsor / Employer"
 *   certificate number       ← "Certificate Number", else "Member ID / Certificate #"
 * Null for everything else, and for any box about another plan.
 */
export function onboardingPlanVal(field: { name?: unknown; binding?: unknown }, p: any): string | null {
  const n = String(field?.name || "").toLowerCase();
  const b = String(field?.binding || "").toLowerCase();
  if (isOtherPlanBox(n, b)) return null;
  const ins: any = p?.insurance && typeof p.insurance === "object" ? p.insurance : {};
  const member = b ? b.split(".").pop() || "" : "";
  // By name, the box must END in the ask — `employer_address` and
  // `certificate_effective_date` are other questions.
  const sponsorBox = member
    ? ["employer_name", "sponsor_name", "plan_sponsor", "plan_sponsor_name"].includes(member)
    : /(^|_)(employer|sponsor)(_name)?$/.test(n);
  if (sponsorBox) return ins.plan_sponsor || p?.plan_sponsor || null;
  const certificateBox = member
    ? member === "certificate_number"
    : /(^|_)certificate(_id)?(_(no|num|number))?$/.test(n);
  if (certificateBox) {
    return ins.certificate_number || p?.certificate_number || ins.member_id || p?.insurance_id || p?.member_id || null;
  }
  return null;
}

/** The Not-applicable box of a "have you applied to a provincial plan" ask
 *  (RBC: `has_the_patient_applied_for_reimbursement_under_a_provincial_plan_na`). */
const PROVINCIAL_CLAIM_NA = /^(?=.*(provincial|pharmacare|public_plan|public_program))(?=.*(appl|reimburs)).*_(na|n_a|not_applicable)$/;

/**
 * Seed confirmed demographics into the schema field values so the
 * questionnaire treats them as already answered — showing them on review
 * instead of re-asking name / DOB / address / etc. Mutates `schemaFields`.
 *
 * The boxes it fills from the first page's PLAN answers (sponsor, certificate,
 * the provincial N/A) are marked `from_first_page` and their names returned.
 * Those go back with the submission (`untouchedBaseline`): the Mac's auto-fill
 * has no route from the profile to these boxes, so a value the patient saw
 * "on file" would otherwise reach the fax blank. The demographics do not — the
 * Mac fills those from the profile itself. The mark is also what lets the
 * questionnaire read a seeded Yes/No/N-A as answered, and nothing else.
 */
export function seedSchemaDemographics(
  schemaFields: RawField[],
  initialProfile: PatientProfile | Record<string, any> | null | undefined,
  readerSubjects: string[],
): string[] {
  const carried: string[] = [];
  if (!initialProfile || typeof initialProfile !== "object") return carried;
  const p: any = initialProfile;
  // Both halves or nothing — mirrors `_seed_profile_demographics` in
  // routers/bridge.py. A plan-member session's preload blob carries the
  // patient's FIRST name alone (for the role banner), and since the
  // M6453 re-tag the patient's name box sits on that leg: joining what
  // is present would seed half a name into it (principle 16).
  const full = p.first_name && p.last_name
    ? `${p.first_name} ${p.last_name}`.trim()
    : "";
  // A box that is ABOUT THE PLAN MEMBER, read by someone who is not
  // them — the adult dependant filling their spouse's plan details
  // (case imp_7014a9c4). Mirrors `_plan_member_demo_val` in
  // routers/bridge.py: address and contact number come from the
  // patient's chart (same household, and a wrong postal code is
  // cheaper to fix than a blank one is to type); the EMAIL and every
  // identifying box come from the plan member's own profile keys or
  // stay empty, because the carrier writes to that address and the
  // patient's is not it (principle 16). The plan member corrects all
  // of it at the review-and-sign step.
  const planMemberDemoVal = (name: string) => {
    const n = String(name || "").toLowerCase();
    if (n.includes("insurance") || n.includes("dependent_cardholder")) return null;
    if (n.includes("email")) return p.plan_member_email || null;
    if (n.includes("full_name")) {
      return p.plan_member_first_name && p.plan_member_last_name
        ? `${p.plan_member_first_name} ${p.plan_member_last_name}`.trim()
        : null;
    }
    if (n.includes("first_name") || n.includes("given_name")) return p.plan_member_first_name || null;
    if (n.includes("last_name") || n.includes("surname")) return p.plan_member_last_name || null;
    if (n.includes("date_of_birth") || n.includes("_dob")) return p.plan_member_dob || null;
    if (n.includes("health_card") || n.includes("health_care")) return p.plan_member_health_card_number || null;
    if (n.includes("phone")) {
      if (isWorkPhoneBox(n)) return p.plan_member_phone_work || null;
      return p.plan_member_phone || p.phone || null;
    }
    if (n.includes("postal")) return p.postal_code || p.postal || null;
    if (n.includes("street_address") || (n.includes("address") && !n.includes("email"))) return p.address || p.street_address || null;
    if (n.includes("city")) return p.city || null;
    if (n.includes("province")) return p.province || null;
    return null;
  };
  const demoVal = (name: string) => {
    const n = String(name || "").toLowerCase();
    // Card-owned PRIMARY insurance (Part A): carry the policy/group # and
    // member ID forward from the Confirm Details card so the patient
    // isn't asked to re-enter them. The card writes plan_number +
    // insurance_id (flat) and an `insurance` sub-doc. Exclude the Part-B
    // "other plan" fields — those are gated coordination-of-benefits Qs.
    const ins: any = (p.insurance && typeof p.insurance === "object") ? p.insurance : {};
    if (!n.includes("other_plan")) {
      if (n.includes("policy_group") || (n.includes("policy") && n.includes("number"))) {
        return p.plan_number || p.policy_number || ins.plan_number || ins.policy_number || null;
      }
      if (n.includes("member_id") || (n.includes("insurance") && n.includes("id") && !n.includes("dependent"))) {
        return p.insurance_id || p.member_id || ins.member_id || ins.id || null;
      }
    }
    // Everything else insurance/Part-B is NOT card-owned — leave to the gated Qs.
    if (n.includes("insurance") || n.includes("dependent_cardholder")) return null;
    if (n.includes("full_name")) return full || null;
    if (n.includes("first_name") || n.includes("given_name")) return p.first_name || null;
    if (n.includes("last_name") || n.includes("surname")) return p.last_name || null;
    if (n.includes("date_of_birth") || n.includes("_dob")) return p.date_of_birth || null;
    if (n.includes("email")) return p.email || null;
    if (n.includes("postal")) return p.postal_code || p.postal || null;
    if (n.includes("street_address") || (n.includes("address") && !n.includes("email"))) return p.address || p.street_address || null;
    if (n.includes("city")) return p.city || null;
    if (n.includes("province")) return p.province || null;
    // The chart's one `phone` is the primary / mobile number. A WORK box is a
    // different number and stays empty unless the chart holds one (case
    // imp_a2bb0615) — mirrors `is_work_phone_box` in routers/bridge.py.
    if (n.includes("phone")) return isWorkPhoneBox(n) ? (p.phone_work || null) : (p.phone || null);
    if (n.includes("health_card") || n.includes("health_care")) return p.health_care_number || p.health_card_number || null;
    return null;
  };
  const isEmpty = (f: RawField) => f && typeof f === "object" && f.name && (f.value == null || f.value === "");
  const byName = new Map<string, RawField>();
  for (const f of schemaFields) if (f && typeof f === "object" && f.name) byName.set(String(f.name).toLowerCase(), f);
  for (const f of schemaFields) {
    if (!isEmpty(f)) continue;
    // A box a person deliberately cleared is an answer (principle 7).
    if (String(f.auto_fill_source || "").includes("user_correction")) continue;
    const planVal = onboardingPlanVal(f, p);
    if (planVal) {
      f.value = String(planVal);
      f.from_first_page = true;
      carried.push(f.name);
      continue;
    }
    // Provincial claim: the first page asked "Do you also have provincial drug
    // coverage?". No there makes "have you applied to a provincial plan" Not
    // applicable; Yes leaves it to be asked (Michael, 2026-10-28). Only when
    // the form prints an N/A box and nothing in the trio is ticked.
    const n = String(f.name).toLowerCase();
    if (p.has_public_insurance === false && PROVINCIAL_CLAIM_NA.test(n)) {
      const base = n.replace(/_(na|n_a|not_applicable)$/, "");
      const yes = byName.get(`${base}_yes`);
      const no = byName.get(`${base}_no`);
      if ((yes || no) && (!yes || isEmpty(yes)) && (!no || isEmpty(no))) {
        f.value = "✓";
        f.from_first_page = true;
        carried.push(f.name);
      }
      continue;
    }
    const subject = String(f.subject || "patient").toLowerCase();
    const aboutTheOtherParty =
      subject === "plan_member" && !readerSubjects.includes("plan_member");
    const v = aboutTheOtherParty ? planMemberDemoVal(f.name) : demoVal(f.name);
    if (v) f.value = String(v);
  }
  return carried;
}

/** The submit baseline: every pre-filled value EXCEPT the boxes filled from
 *  the first page (`from_first_page`), which must go back with the
 *  submission (see seedSchemaDemographics). */
export function untouchedBaseline(schemaFields: RawField[]): Record<string, string> {
  const out = prefilledValues(schemaFields);
  for (const f of schemaFields) if (f && typeof f === "object" && f.from_first_page) delete out[f.name];
  return out;
}

/** The values the schema arrives with (sealed or seeded). Anything the signer
 *  leaves equal to these is not sent back, so an untouched pre-fill never
 *  round-trips onto the canonical. */
export function prefilledValues(schemaFields: RawField[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of schemaFields) {
    if (f && typeof f === "object" && f.name && f.value != null && f.value !== "") {
      out[f.name] = String(f.value);
    }
  }
  return out;
}

/**
 * Every signature line this signer owns, ordered by page then position
 * (imp_31e369a1). Date lines are excluded — they are stamped when their
 * signature line is signed. Falls back to the backend-chosen single field
 * when the schema's entries carry no usable signature bboxes; empty on v1.
 */
export function signatureLinesFrom(
  schemaFields: RawField[],
  schemaVersion: number,
  signatureField: SignatureLine | null,
): SignatureLine[] {
  if (schemaVersion < 2) return [];
  const lines: SignatureLine[] = schemaFields
    .filter(
      (f) =>
        f &&
        typeof f === "object" &&
        /signature/i.test(String(f.name || "")) &&
        !/date/i.test(String(f.name || "")) &&
        Array.isArray(f.bbox) &&
        f.bbox.length >= 4 &&
        Number.isFinite(Number(f.page))
    )
    .map((f) => ({
      name: f.name,
      page: Number(f.page),
      bbox: f.bbox.map((v: any) => Number(v)) as BBox,
    }))
    .sort((a, b) => a.page - b.page || a.bbox[1] - b.bbox[1]);
  return lines.length > 0 ? lines : signatureField ? [signatureField] : [];
}

/**
 * The date box paired with one signature line. The key must match the
 * schema's actual field name — deriving it by string surgery stranded the
 * value on an orphan key whenever the dispatched schema's casing differed (v1
 * dispatches keyed lowercase machine_names; v2 uses Title_Case names), and the
 * date silently never rendered. Candidates are resolved against the schema
 * case-insensitively; suffix blocks (_B1) and plan-member signers keep working
 * via the candidate forms.
 */
export function resolveSignatureDateField(sigFieldName: string | undefined, schemaFields: RawField[]): string {
  const cands: string[] = [];
  if (sigFieldName) {
    const m = sigFieldName.match(/^(.*[Ss]ignature)(_[A-Za-z]*\d+)$/);
    cands.push(m ? `${m[1]}_Date${m[2]}` : `${sigFieldName}_Date`);
  }
  cands.push("patient_signature_date"); // legacy v1 fallback
  const names = (schemaFields || []).map((f: any) =>
    typeof f === "string" ? f : (f && f.name) || ""
  );
  let dateField: string | null | undefined = null;
  for (const c of cands) {
    dateField = names.find((n: string) => n.toLowerCase() === c.toLowerCase());
    if (dateField) break;
  }
  if (!dateField) {
    // Any signer-scoped signature-date line (never prescriber's).
    dateField = names.find((n: string) =>
      /^(?!.*(prescriber|physician|provider|doctor)).*signature.*date/i.test(n)
    );
  }
  return dateField || cands[0];
}
